import React, { useEffect, useRef } from "react";

/**
 * CaptchaBadge: Renders CAPTCHA text as a graphical canvas image.
 * Prevents text selection, dragging, copying, or DOM text inspection.
 */
const CaptchaBadge = ({
  code = "",
  width = 120,
  height = 38,
  textColor = "#0f172a",
  bgColor = "#f1f5f9",
  fontSize = 19,
  className = "",
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !code) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Background subtle noise lines
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(148, 163, 184, ${0.15 + Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Subtle noise dots
    for (let i = 0; i < 25; i++) {
      ctx.fillStyle = `rgba(100, 116, 139, ${0.2 + Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw Characters
    const charCount = code.length;
    const availableWidth = width - 18;
    const spacing = availableWidth / charCount;
    const startX = 10 + spacing / 2;

    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    for (let i = 0; i < charCount; i++) {
      const char = code[i];
      const x = startX + i * spacing;
      const y = height / 2 + (Math.random() - 0.5) * 3;
      const angle = (Math.random() - 0.5) * 0.35; // Slight tilt

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);

      // Random font styling for character
      const fontFamilies = ["'Courier New', Courier, monospace", "'Arial', sans-serif", "'Trebuchet MS', sans-serif"];
      const fontFam = fontFamilies[i % fontFamilies.length];
      ctx.font = `bold ${fontSize}px ${fontFam}`;
      ctx.fillStyle = textColor;
      ctx.fillText(char, 0, 0);

      ctx.restore();
    }
  }, [code, width, height, textColor, bgColor, fontSize]);

  return (
    <canvas
      ref={canvasRef}
      className={`captcha-canvas-badge ${className}`}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      style={{
        display: "inline-block",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        pointerEvents: "none",
        cursor: "default",
        borderRadius: "8px",
        verticalAlign: "middle",
      }}
      aria-label="Security CAPTCHA verification image"
    />
  );
};

export default CaptchaBadge;
